import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

export const WorkPage = () => {
  const projects = [
    {
      title: 'EcoShop',
      category: 'E-commerce',
      description: 'A sustainable products marketplace focused on eco-friendly living.',
      challenge: 'The client needed an online store that reflected their environmental values while providing a seamless shopping experience.',
      solution: 'We created a clean, green-themed Shopify store with intuitive navigation, product filtering, and educational content about sustainability.',
      results: [
        '150% increase in online conversions',
        '3.2s average page load time',
        '45% increase in mobile transactions',
        '200+ products successfully migrated',
      ],
      image: 'https://images.pexels.com/photos/18105/pexels-photo.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      tags: ['Shopify', 'E-commerce', 'SEO', 'Mobile-First'],
      note: 'Sample project for demonstration',
    },
    {
      title: 'TechStart Hub',
      category: 'SaaS Landing Page',
      description: 'A B2B platform connecting startups with enterprise customers.',
      challenge: 'The startup needed a high-converting landing page to generate qualified leads for their beta launch.',
      solution: 'We designed a conversion-focused single page with clear value propositions, social proof, and a multi-step signup form.',
      results: [
        '500+ qualified leads per month',
        '12% conversion rate (industry avg: 2-5%)',
        '82% mobile traffic conversion',
        'Featured on Product Hunt',
      ],
      image: 'https://images.pexels.com/photos/12882904/pexels-photo-12882904.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      tags: ['Landing Page', 'SaaS', 'Lead Gen', 'Conversion'],
      note: 'Sample project for demonstration',
    },
    {
      title: 'Urban Wellness',
      category: 'Service Business',
      description: 'A modern yoga and wellness studio offering classes and workshops.',
      challenge: 'The studio was losing bookings to competitors with better online presence and booking systems.',
      solution: 'We built a beautiful, calming website with integrated online booking, class schedules, and instructor profiles.',
      results: [
        '200% increase in organic traffic',
        '60% of bookings now made online',
        '4.8/5 average user rating',
        '35% increase in new member signups',
      ],
      image: 'https://images.pexels.com/photos/3194518/pexels-photo-3194518.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      tags: ['WordPress', 'Booking System', 'SEO', 'Local Business'],
      note: 'Sample project for demonstration',
    },
    {
      title: 'FreshBite Delivery',
      category: 'Food & Beverage',
      description: 'A meal delivery service specializing in healthy, chef-prepared meals.',
      challenge: 'The brand needed to stand out in a crowded market and streamline their ordering process.',
      solution: 'We created a vibrant, appetite-appealing website with custom meal builder, subscription management, and delivery tracking.',
      results: [
        '180% increase in subscription signups',
        '25% reduction in customer service inquiries',
        '90% customer satisfaction score',
        '40% increase in average order value',
      ],
      image: 'https://images.pexels.com/photos/1181346/pexels-photo-1181346.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      tags: ['Custom Dev', 'Subscription', 'E-commerce', 'Mobile App'],
      note: 'Sample project for demonstration',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-32" data-testid="work-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6" data-testid="work-hero-heading">
            Our Work
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-3xl mx-auto" data-testid="work-hero-description">
            Real projects, real results. See how we've helped businesses grow their online presence.
          </p>
        </div>
      </section>

      {/* Promo Banner */}
      <section className="py-8 bg-slate-50 border-b border-slate-200" data-testid="work-promo-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
            <div className="md:w-48 flex-shrink-0">
              <img
                src="https://images.pexels.com/photos/29506607/pexels-photo-29506607.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Websites in 7 days promotional offer - free website audit"
                className="rounded-lg w-full"
                data-testid="work-promo-image"
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2" data-testid="work-promo-heading">
                Get Your Project Started Today
              </h3>
              <p className="text-slate-600 mb-4" data-testid="work-promo-description">
                Join our growing list of satisfied clients. Request a free website audit and discover how we can help your business.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-[#10B981] text-white hover:bg-[#059669] rounded-full px-6 py-3 font-bold transition-all duration-300 hover:-translate-y-0.5"
                data-testid="work-promo-cta"
              >
                Request Free Audit
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-20 md:py-32 bg-white" data-testid="work-projects-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-24">
            {projects.map((project, index) => (
              <div key={index} className="" data-testid={`project-${index}`}>
                {/* Project Image */}
                <div className="rounded-2xl overflow-hidden shadow-lg mb-8">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-[400px] object-cover"
                    data-testid={`project-image-${index}`}
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-12">
                  {/* Left: Title and Description */}
                  <div className="md:col-span-1">
                    <div className="inline-block bg-[#10B981]/10 text-[#10B981] px-4 py-1.5 rounded-full font-medium text-sm mb-4" data-testid={`project-category-${index}`}>
                      {project.category}
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid={`project-title-${index}`}>
                      {project.title}
                    </h2>
                    <p className="text-slate-600 mb-4" data-testid={`project-description-${index}`}>
                      {project.description}
                    </p>
                    <p className="text-sm text-slate-500 italic mb-4" data-testid={`project-note-${index}`}>
                      {project.note}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm font-mono"
                          data-testid={`project-tag-${index}-${i}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Right: Challenge, Solution, Results */}
                  <div className="md:col-span-2 space-y-8">
                    <div>
                      <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">The Challenge</h3>
                      <p className="text-slate-600" data-testid={`project-challenge-${index}`}>
                        {project.challenge}
                      </p>
                    </div>

                    <div>
                      <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">Our Solution</h3>
                      <p className="text-slate-600" data-testid={`project-solution-${index}`}>
                        {project.solution}
                      </p>
                    </div>

                    <div>
                      <h3 className="font-heading font-bold text-xl text-slate-900 mb-4">Results</h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {project.results.map((result, i) => (
                          <div
                            key={i}
                            className="bg-slate-50 border border-slate-200 rounded-lg p-4"
                            data-testid={`project-result-${index}-${i}`}
                          >
                            <div className="flex items-start gap-2">
                              <ExternalLink size={18} className="text-[#10B981] mt-0.5 flex-shrink-0" />
                              <span className="text-slate-700 font-medium">{result}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {index < projects.length - 1 && <div className="border-b border-slate-200 mt-16"></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="work-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6" data-testid="work-cta-heading">
            Ready to be our next success story?
          </h2>
          <p className="text-lg text-slate-600 mb-8" data-testid="work-cta-description">
            Let's create something amazing together.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#10B981] text-white hover:bg-[#059669] rounded-full px-8 py-4 font-bold shadow-lg shadow-green-500/20 hover:shadow-green-500/40 transition-all duration-300 hover:-translate-y-1 text-lg uppercase tracking-wide"
            data-testid="work-cta-button"
          >
            Start Your Project
          </Link>
        </div>
      </section>
    </div>
  );
};
